import { OrgRefScope, OrgScope } from "../../../../types/gqlTypes/graphql"
import { User } from "../../../mp-database"
import { FiltersState } from "../../states"
import { getEffectiveDistrictId } from "../../utils/districtUtils"

export const buildOrgRefScopeByUser = (user: User): OrgRefScope => {
  switch (user.role) {
    case "Super Administrator":
    case "ACT Admin": {
      return {
        scope: "SYSTEM",
        districtId: user.schoolDistrict || ""
      }
    }
    case "Organization Admin": {
      return {
        scope: "ORGANIZATION",
        organizationId: user.organizationId,
        districtId: user.schoolDistrict
      }
    }
    case "School Admin": {
      return {
        scope: "SCHOOL",
        districtId: user.schoolDistrict,
        schoolId: user.school || user.schools[0]
      }
    }
    case "Instructor":
    case "Teacher": {
      return {
        scope: "TEACHER",
        districtId: user.schoolDistrict,
        schoolId: user.school || user.schools[0],
        teacherId: user.id
      }
    }
    case "Student": {
      return {
        scope: "STUDENT",
        districtId: user.schoolDistrict,
        schoolId: user.school || user.schools[0]
      }
    }
    default: {
      return {
        scope: "DISTRICT",
        districtId: user.schoolDistrict
      }
    }
  }
}

export const getOrgRefScope = (args: {
  user: User
  filters: FiltersState
  entityId?: string
  isGraphQLProvider?: boolean
  useUserScope?: boolean
}): OrgRefScope => {
  const {
    user,
    filters,
    entityId,
    isGraphQLProvider = false,
    useUserScope = false
  } = args

  const userOrgRefScope = buildOrgRefScopeByUser(user)

  // Extract all org filters - use utility for consistent resolution
  const organizationId = filters?.organization?.[0] || null
  const districtId = getEffectiveDistrictId(filters, user, entityId)
  const schoolId = filters?.school?.[0] || null
  const teacherId = filters?.teacher?.[0] || null
  const classId = filters?.class?.[0] || null
  const studentId = filters?.student?.[0] || null

  // Determine the scope based on the most specific filter
  let scope: OrgScope = userOrgRefScope.scope
  if (studentId) {
    scope = "STUDENT"
  } else if (classId) {
    scope = "CLASS"
  } else if (teacherId) {
    scope = "TEACHER"
  } else if (schoolId) {
    scope = "SCHOOL"
  } else if (districtId) {
    scope = "DISTRICT"
  } else if (organizationId) {
    scope = "ORGANIZATION"
  }

  if (isGraphQLProvider) {
    if (!Object.keys(filters).length) {
      return userOrgRefScope
    }

    return {
      ...((organizationId || userOrgRefScope.organizationId) && {
        organizationId: organizationId || userOrgRefScope.organizationId
      }),
      ...((districtId || userOrgRefScope.districtId) && {
        districtId: districtId || userOrgRefScope.districtId
      }),
      ...((schoolId || userOrgRefScope.schoolId) && {
        schoolId: schoolId || userOrgRefScope.schoolId
      }),
      ...((teacherId || userOrgRefScope.teacherId) && {
        teacherId: teacherId || userOrgRefScope.teacherId
      }),
      ...(classId && { classId }),
      ...((studentId || userOrgRefScope.studentId) && {
        studentId: studentId || userOrgRefScope.studentId
      }),
      scope: useUserScope ? userOrgRefScope.scope : scope
    }
  }

  return {
    scope: scope as OrgScope,
    organizationId,
    districtId,
    schoolId,
    teacherId,
    classId,
    studentId
  }
}
